import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, InputBase, Paper, Tooltip, Typography } from '@mui/material';
import { Send, Mic, MicOff, AttachFile, Cancel } from '@mui/icons-material';
import { useAtom, useAtomValue } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import { ChatMessage } from '../../models/chat/ChatMessage';
import { sendMessageToChatbot, useChatState } from '../../utils/client/messageUtils';
import { generateWelcomeMessage } from '../../utils/server/chatbotUtils';
import { getActiveTenantId } from '../../utils/server/authUtils';
import { loadingChatMessageState } from '../../state/chat/LoadingChatMessageState';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { useAuth } from '../../../context/AuthContext';
import { showToastError } from '../../utils/common/toast';
import { useAppTranslation } from '../../hooks/useAppTranslation';

const SPEECH_LANGUAGE_MAP = {
    de: 'de-DE',
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    hi: 'hi-IN',
    it: 'it-IT',
    pt: 'pt-BR',
    ru: 'ru-RU',
    zh: 'zh-CN',
    ja: 'ja-JP',
};

function resolveSpeechLanguage(appLanguage) {
    if (!appLanguage || typeof appLanguage !== 'string') return 'de-DE';
    if (appLanguage.includes('-')) return appLanguage;
    return SPEECH_LANGUAGE_MAP[appLanguage] || 'de-DE';
}

function getLanguageLabel(localeCode) {
    const labels = {
        'de-DE': 'Deutsch',
        'en-US': 'English',
        'fr-FR': 'Français',
        'es-ES': 'Español',
        'hi-IN': 'Hindi',
        'it-IT': 'Italiano',
        'pt-BR': 'Português',
        'ru-RU': 'Русский',
        'zh-CN': 'Chinese',
        'ja-JP': 'Japanese',
    };

    return labels[localeCode] || localeCode;
}

function normalizeHexColor(input, fallback = '#172983') {
    const value = String(input || '').trim();
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value;
    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
        return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
    }
    return fallback;
}

function shiftHexColor(hexColor, amount) {
    const normalized = normalizeHexColor(hexColor);
    const channels = normalized.match(/[0-9a-fA-F]{2}/g);

    if (!channels) return normalized;

    const shifted = channels.map((channel) => {
        const next = Math.max(0, Math.min(255, parseInt(channel, 16) + amount));
        return next.toString(16).padStart(2, '0');
    });

    return `#${shifted.join('')}`;
}

function hexToRgba(hexColor, alpha) {
    const normalized = normalizeHexColor(hexColor);
    const channels = normalized.match(/[0-9a-fA-F]{2}/g);

    if (!channels) {
        return `rgba(23, 41, 131, ${alpha})`;
    }

    const [r, g, b] = channels.map((channel) => parseInt(channel, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function ChatInput() {
    const chatState = useChatState();
    const [currentMessage, setCurrentMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [isSpeechSupported, setIsSpeechSupported] = useState(false);

    const [isLoading] = useAtom(loadingChatMessageState);
    const config = useAtomValue(chatbotConfigState);
    const { t } = useAppTranslation();

    const fmt = (template, vars = {}) =>
        String(template || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ''));

    const { token } = useAuth();
    const activeTenantId = useMemo(() => (token ? getActiveTenantId({ token }) : null), [token]);

    const recognitionRef = useRef(null);
    const textBeforeSpeech = useRef('');

    const speechLanguage = useMemo(() => resolveSpeechLanguage(config.language), [config.language]);
    const speechLanguageLabel = useMemo(() => getLanguageLabel(speechLanguage), [speechLanguage]);
    const primaryColor = useMemo(() => normalizeHexColor(config.primaryColor || '#172983'), [config.primaryColor]);
    const primaryColorHover = useMemo(() => shiftHexColor(primaryColor, -18), [primaryColor]);
    const primaryColorShadow = useMemo(() => hexToRgba(primaryColor, 0.22), [primaryColor]);
    const isSubmitting = isLoading || chatState.isLoading;

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (SpeechRecognition) {
            setIsSpeechSupported(true);
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.maxAlternatives = 1;
            recognitionRef.current.lang = speechLanguage;

            recognitionRef.current.onstart = () => {
                setIsListening(true);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = '';
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }

                const sessionTranscript = (finalTranscript || interimTranscript);
                if (sessionTranscript) {
                    const prefix = textBeforeSpeech.current.trim();
                    const spacer = prefix ? ' ' : '';
                    setCurrentMessage(prefix + spacer + sessionTranscript);
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error', event.error);
                setIsListening(false); // Reset state immediately on error
                
                if (event.error === 'not-allowed') {
                    showToastError(t.chatInputMicDenied);
                } else if (event.error === 'no-speech') {
                    return;
                } else {
                    showToastError(fmt(t.chatInputVoiceError, { error: event.error }));
                }
            };
        } else {
            console.warn('Speech API not supported in this browser.');
            setIsSpeechSupported(false);
        }
    }, []);

    useEffect(() => {
        if (recognitionRef.current) {
            recognitionRef.current.lang = speechLanguage;
        }
    }, [speechLanguage]);

    const toggleListening = () => {
        if (!isSpeechSupported || !recognitionRef.current) {
            showToastError(t.chatInputSpeechNotSupported);
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            textBeforeSpeech.current = currentMessage; 
            
            try {
                recognitionRef.current.lang = speechLanguage;
                recognitionRef.current.start();
            } catch (err) {
                console.error('Mic start error:', err);
                try {
                    recognitionRef.current.stop();
                    setTimeout(() => {
                        try { recognitionRef.current.start(); } catch(e){}
                    }, 300);
                } catch(e) {}
            }
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxBytes = 5 * 1024 * 1024;

        if (!allowedMimeTypes.includes(file.type)) {
            showToastError(t.chatInputImageTypeUnsupported);
            return;
        }

        if (file.size > maxBytes) {
            showToastError(t.chatInputImageTooLarge);
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSelectedImage({ file, preview: reader.result });
        };
        reader.readAsDataURL(file);
    };

    const clearImage = () => setSelectedImage(null);

    const onSendMessage = async () => {
        if (isListening && recognitionRef.current) {
            recognitionRef.current.stop();
        }

        if (currentMessage.trim() === '' && !selectedImage) return;
        if (isSubmitting) return;

        const messageText = currentMessage.trim();
        const imagePayload = selectedImage ? selectedImage.preview : null;

        setCurrentMessage('');
        textBeforeSpeech.current = '';
        setSelectedImage(null);

        await sendMessageToChatbot(messageText, chatState, imagePayload);
    };

    const hookRunning = useRef(false);

    const loadWelcomeMessage = useCallback(async ({ force = false } = {}) => {
        if (!token) return;
        if (hookRunning.current) return;
        if (!force && (chatState.chatMessages.length > 0 || chatState.isLoading)) return;

        hookRunning.current = true;
        chatState.setIsLoading(true);

        try {
            const message = await generateWelcomeMessage(token, activeTenantId);
            chatState.setChatMessages([ChatMessage.botMessage(message.aiResponse)]);
        } finally {
            chatState.setChatId(uuidv4());
            chatState.setIsLoading(false);
            hookRunning.current = false;
        }
    }, [activeTenantId, chatState, token]);

    useEffect(() => {
        void loadWelcomeMessage();
    }, [loadWelcomeMessage]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const handleScopeChange = () => {
            setCurrentMessage('');
            setSelectedImage(null);
            textBeforeSpeech.current = '';

            if (recognitionRef.current && isListening) {
                try {
                    recognitionRef.current.stop();
                } catch (error) {
                    console.error('Mic stop error during scope switch:', error);
                }
            }

            chatState.setChatMessages([]);
            chatState.setChatId(uuidv4());
            void loadWelcomeMessage({ force: true });
        };

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, [chatState, isListening, loadWelcomeMessage]);

    const iconSx = {
        width: 38,
        height: 38,
        borderRadius: '13px',
        color: 'var(--text-color-light)',
        backgroundColor: 'var(--component-bg-light)',
        border: '1px solid var(--border-color)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'var(--component-bg)',
            borderColor: primaryColor,
            transform: 'translateY(-1px)',
        },
        '&:focus-visible': {
            outline: '2px solid var(--accent-color)',
            outlineOffset: 2,
        },
    };

    const sendButtonSx = {
        width: 42,
        height: 42,
        borderRadius: '15px',
        color: 'white',
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorHover} 100%)`,
        boxShadow: `0 10px 20px ${primaryColorShadow}`,
        transition: 'all 0.2s ease',
        '&:hover': {
            background: `linear-gradient(135deg, ${primaryColorHover} 0%, ${shiftHexColor(primaryColorHover, -8)} 100%)`,
            transform: 'translateY(-1px)',
        },
        '&:focus-visible': {
            outline: '2px solid var(--accent-color)',
            outlineOffset: 2,
        },
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.75)',
            background: 'var(--button-disabled-bg)',
            boxShadow: 'none',
        },
    };

    return (
        <Box sx={{ px: 1.5, pb: 1.4, pt: 1 }}>
            {selectedImage && (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        mb: 1.2,
                        px: 1,
                        py: 0.9,
                        borderRadius: 3,
                        border: '1px solid var(--text-color-light)',
                        backgroundColor: 'var(--component-bg)',
                    }}
                >
                    <Box
                        component='img'
                        src={selectedImage.preview}
                        alt='Preview'
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            objectFit: 'cover',
                            border: '1px solid var(--text-color-light)',
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body2' sx={{ fontWeight: 600, color: 'var(--text-color)' }}>
                            Image ready to send
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'var(--text-color-light)' }}>
                            {selectedImage.file?.name || 'Attached image'}
                        </Typography>
                    </Box>
                    <IconButton size='small' onClick={clearImage} sx={{ ...iconSx, width: 32, height: 32, borderRadius: 2.5 }} aria-label={t.chatInputRemoveImage}>
                        <Cancel fontSize='small' />
                    </IconButton>
                </Paper>
            )}

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    border: '1px solid var(--text-color-light)',
                    backgroundColor: 'var(--component-bg)',
                    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.06)',
                    px: 0.9,
                    py: 0.8,
                    transition: 'all 0.2s ease',
                    '&:focus-within': {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 3px ${hexToRgba(primaryColor, 0.14)}`,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.9 }}>
                    <Tooltip title={t.chatInputUploadImage}>
                        <IconButton component='label' sx={iconSx} aria-label={t.chatInputUploadImage}>
                            <input hidden type='file' accept='.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp' onChange={handleImageSelect} />
                            <AttachFile fontSize='small' />
                        </IconButton>
                    </Tooltip>

                    <InputBase
                        sx={{
                            px: 0.9,
                            py: 0.45,
                            flex: 1,
                            fontSize: '0.96rem',
                            lineHeight: 1.45,
                            alignSelf: 'center',
                            color: 'var(--text-color)',
                            '& textarea::placeholder, & input::placeholder': {
                                color: 'var(--text-color-light)',
                                opacity: 1,
                            },
                        }}
                        multiline
                        minRows={1}
                        maxRows={4}
                        placeholder={isListening ? fmt(t.chatInputListeningPlaceholder, { lang: speechLanguageLabel }) : t.chatInputTypePlaceholder}
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        inputProps={{ 'aria-label': t.chatInputMessageInputAria }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                onSendMessage();
                            }
                        }}
                    />

                    {isSpeechSupported && (
                        <Tooltip title={isListening ? t.chatInputStopVoice : fmt(t.chatInputStartVoice, { lang: speechLanguageLabel })}>
                            <IconButton
                                onClick={toggleListening}
                                aria-label={isListening ? t.chatInputStopVoice : fmt(t.chatInputStartVoiceAria, { lang: speechLanguageLabel })}
                                sx={{
                                    ...iconSx,
                                    color: isListening ? '#dc2626' : '#52607a',
                                    borderColor: isListening ? 'rgba(252, 165, 165, 0.85)' : 'var(--border-color)',
                                    backgroundColor: isListening ? 'rgba(254, 242, 242, 1)' : 'var(--component-bg-light)',
                                    animation: isListening ? 'chatPulse 1.5s infinite' : 'none',
                                    '@keyframes chatPulse': {
                                        '0%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.35)' },
                                        '70%': { boxShadow: '0 0 0 12px rgba(239, 68, 68, 0)' },
                                        '100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' },
                                    },
                                }}
                            >
                                {isListening ? <MicOff fontSize='small' /> : <Mic fontSize='small' />}
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title={t.chatInputSend}>
                        <span>
                            <IconButton
                                disabled={isSubmitting || (!currentMessage.trim() && !selectedImage)}
                                onClick={onSendMessage}
                                sx={sendButtonSx}
                                aria-label={t.chatInputSend}
                            >
                                <Send fontSize='small' />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Paper>
        </Box>
    );
}